import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioAccommodationAddonObject from '@/entities/ratioAccommodationAddon/interface'

import {
  ALL_PACKAGES_QUERY,
  SINGLE_PACKAGE_QUERY,
  PACKAGES_DEPENDENTS_QUERY,
  CREATE_PACKAGE_MUTATION,
  UPDATE_PACKAGE_MUTATION,
  DELETE_PACKAGE_MUTATION,
  FORCE_DELETE_PACKAGE_MUTATION,
  ADD_PACKAGE_ADDONS_MUTATION,
  REMOVE_PACKAGE_ADDONS_MUTATION,
  CREATE_PACKAGE_ADDONS_MUTATION,
  DELETE_PACKAGE_ADDONS_MUTATION,
  UPDATE_PACKAGE_ADDONS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioFamilyPackageRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_PACKAGE_QUERY, variables).then((data: any) => {
      return hydrateObject(data.package)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_PACKAGES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.packages)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_PACKAGE_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.package),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_PACKAGES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.packages),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(PACKAGES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([PACKAGES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_PACKAGE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.package)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_PACKAGE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.package)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_PACKAGE_MUTATION, variables).then((data: any) => {
      return data.deleteRatioFamilyPackage
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_PACKAGE_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioFamilyPackage
    })
  },

  assignAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        addonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_PACKAGE_ADDONS_MUTATION, variables)
  },

  unassignAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        addonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_PACKAGE_ADDONS_MUTATION, variables)
  },

  createAddons: async (id: string, items: Array<ratioAccommodationAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        addons: items,
      },
    }
    return graphQLClient.request(CREATE_PACKAGE_ADDONS_MUTATION, variables)
  },

  deleteAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        addonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_PACKAGE_ADDONS_MUTATION, variables)
  },

  updateAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        addonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_PACKAGE_ADDONS_ORDER_MUTATION, variables)
  },
}

export default ratioFamilyPackageRepository
